/* eslint-disable arrow-body-style */
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { IModularConfig } from "src/app/factories/modular-configuration-factory";
import { IOneShotWithExtraProductInformation } from "src/app/factories/extra-product-information-factory";
import { ProductSource } from "src/app/data-models/wl-account-setup/wl-config-data.model";
import { environment } from "src/environments/environment";

export interface IIntiaroPublicApiClient {
  getDesignConfigForPricing(customConfigurationId: string): Promise<Response>  
  getProductConfigForPricing(pimTenantId: string, productConfigId: string): Promise<Response> 
  getProductOneShot(brandId: string, productId: string, productSource: ProductSource, context?: string): Observable<IOneShotWithExtraProductInformation>
  getCustomConfigurationOneShot(brandId: string, customConfigurationId: string, productSource: ProductSource, context?: string): Observable<IOneShotWithExtraProductInformation>
}

@Injectable({
  providedIn: "root",
})
export class IntiaroPublicApiClient implements IIntiaroPublicApiClient {

  /** Public API host the configurator bundle uses for each domainTemplate, so the oneshot comes from the same place. */
  private static readonly ONE_SHOT_DOMAINS: Record<ProductSource, string> = {
    [ProductSource.Prod]: environment.publicApiIntiaro,
    [ProductSource.Stage]: environment.publicApiIntiaroStage,
  };

  private readonly headers: HeadersInit = new Headers({"Content-Type": "application/json", "Accept": "application/json"});

  constructor(private httpClient: HttpClient) {}

  public async getDesignConfigForPricing(customConfigurationId: string): Promise<Response> {
    return fetch(`${environment.publicApiIntiaro}/user-configuration/uuid/${customConfigurationId}/`,
      { method: "GET", headers: this.headers,}
    );
  }

  public async getProductConfigForPricing(pimTenantId: string, productConfigId: string): Promise<Response> {
    return fetch(`${environment.publicApiIntiaro}/piminstanceapi/${pimTenantId}/product_instance/${productConfigId}/?environment=prod`,
      { method: "GET", headers: this.headers,}
    );
  }

  /**
   * The oneshot the configurator loads, with the query parameters of OneShotRepository in the configurator bundle.
   * Goes through HttpClient, unlike the pricing calls, so interceptors see it (TokenInterceptor adds the user
   * token, which public-api ignores).
   */
  public getProductOneShot(
    brandId: string,
    productId: string,
    productSource: ProductSource,
    context?: string
  ): Observable<IOneShotWithExtraProductInformation> {
    return this.httpClient.get<IOneShotWithExtraProductInformation>(
      `${IntiaroPublicApiClient.ONE_SHOT_DOMAINS[productSource]}/piminstanceapi/${encodeURIComponent(brandId)}/product_instance/${encodeURIComponent(productId)}/`,
      { params: this.oneShotParams(productSource, context) }
    );
  }

  /** Oneshot of a saved configuration (a design), as the configurator requests it for a configurationId. */
  public getCustomConfigurationOneShot(
    brandId: string,
    customConfigurationId: string,
    productSource: ProductSource,
    context?: string
  ): Observable<IOneShotWithExtraProductInformation> {
    return this.httpClient.get<IOneShotWithExtraProductInformation>(
      `${IntiaroPublicApiClient.ONE_SHOT_DOMAINS[productSource]}/piminstanceapi/${encodeURIComponent(brandId)}/product_instance/bycc/${encodeURIComponent(customConfigurationId)}`,
      { params: this.oneShotParams(productSource, context) }
    );
  }

  /** The configurator adds the PIM context only when it has one, so the same applies here. */
  private oneShotParams(productSource: ProductSource, context?: string): HttpParams {
    const params = new HttpParams()
      .set("environment", productSource)
      .set("api_version", "1.2.3")
      .set("locale", "en_US");

    return context ? params.set("context", context) : params;
  }
}

export class IntiaroPublicApiClientMock implements IIntiaroPublicApiClient {

  public async getDesignConfigForPricing(_customConfigurationId: string): Promise<Response> {
    const response = {
      "product_configuration":{
        "elements": {
          "0000": {
            "element_id": "0000",
            "variables": {},
            "neighbours": {}
          }
        },
        "product_id": "000"
      }
    };
    
    return new Response(JSON.stringify(response));
  }

  public async getProductConfigForPricing(_pimTenantId: string, _productConfigId: string): Promise<Response> {
    const response = {
      "product_configuration":{
        "elements": {
          "0000": {
            "element_id": "0000",
            "variables": {},
            "neighbours": {}
          }
        },
        "product_id": "000"
      }
    };

    return new Response(JSON.stringify(response));
  }

  public getProductOneShot(_brandId: string, _productId: string, _productSource: ProductSource, _context?: string): Observable<IOneShotWithExtraProductInformation> {
    return of({});
  }

  public getCustomConfigurationOneShot(
    _brandId: string,
    _customConfigurationId: string,
    _productSource: ProductSource,
    _context?: string
  ): Observable<IOneShotWithExtraProductInformation> {
    return of({});
  }
}